import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UserPlus, UserCheck, UserX, Users as UsersIcon, X, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AttendeesSectionProps {
  data: {
    expected_attendees: any[];
    actual_attendees: any[];
    special_guests: any[];
    absent_members: any[];
    quorum_required: boolean;
    quorum_threshold: number | null;
  };
  onChange: (updates: any) => void;
  unaId: string;
  isFirstMeeting?: boolean;
}

export function AttendeesSection({ data, onChange, unaId, isFirstMeeting }: AttendeesSectionProps) {
  const { toast } = useToast();
  const [unaMembers, setUnaMembers] = useState<any[]>([]);
  const [treasurySecretary, setTreasurySecretary] = useState<any | null>(null);
  const [guestName, setGuestName] = useState("");
  const [guestTitle, setGuestTitle] = useState("");

  useEffect(() => {
    loadUnaMembers();
    loadTreasurySecretary();
  }, [unaId]);

  const loadUnaMembers = async () => {
    try {
      const { data: members, error } = await supabase
        .from("una_members")
        .select("id, user_id, full_name, role, status")
        .eq("una_id", unaId)
        .eq("status", "active");

      if (error) throw error;
      setUnaMembers(members || []);
    } catch (error) {
      console.error("Error loading members:", error);
      toast({
        title: "Error",
        description: "Could not load UNA members",
        variant: "destructive",
      });
    }
  };

  const loadTreasurySecretary = async () => {
    try {
      // First get the UNA to find the treasury_secretary_id and primary_officer_role
      const { data: una, error: unaError } = await supabase
        .from("unas")
        .select("treasury_secretary_id, primary_officer_role")
        .eq("id", unaId)
        .single();

      if (unaError || !una?.treasury_secretary_id) return;

      const primaryOfficerRole = una.primary_officer_role || "Secretary Treasurer";

      // Then get the profile of the treasury secretary
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("id", una.treasury_secretary_id)
        .single();

      if (profileError) throw profileError;

      if (profile) {
        setTreasurySecretary({
          id: `officer_${profile.id}`, // Prefix to distinguish from member IDs
          user_id: profile.id,
          full_name: profile.full_name || profile.email || primaryOfficerRole,
          role: primaryOfficerRole,
          is_officer: true,
        });
      }
    } catch (error) {
      console.error("Error loading treasury secretary:", error);
    }
  };

  const handleCheckIn = (member: any) => {
    const isPresent = data.actual_attendees.some(a => a.member_id === member.id);
    
    if (isPresent) {
      // Remove from actual attendees and add to absent
      onChange({
        actual_attendees: data.actual_attendees.filter(a => a.member_id !== member.id),
        absent_members: [...data.absent_members, {
          member_id: member.id,
          member_name: member.full_name,
          member_role: member.role,
          is_officer: member.is_officer || false,
        }],
      });
    } else {
      // Add to actual attendees and remove from absent
      onChange({
        actual_attendees: [...data.actual_attendees, {
          member_id: member.id,
          member_name: member.full_name,
          member_role: member.role,
          is_officer: member.is_officer || false,
          checked_in_at: new Date().toISOString(),
        }],
        absent_members: data.absent_members.filter(a => a.member_id !== member.id),
      });
    }
  };

  const addGuest = () => {
    if (!guestName.trim()) return;

    onChange({
      special_guests: [...data.special_guests, {
        name: guestName,
        title: guestTitle,
        added_at: new Date().toISOString(),
      }],
    });

    setGuestName("");
    setGuestTitle("");
  };

  const removeGuest = (index: number) => {
    onChange({
      special_guests: data.special_guests.filter((_, i) => i !== index),
    });
  };

  const renderAttendeeCard = (member: any) => {
    const isPresent = data.actual_attendees.some(a => a.member_id === member.id);
    
    return (
      <Card
        key={member.id}
        className="p-3 flex items-center justify-between hover:bg-muted/50 transition-colors cursor-pointer"
        onClick={() => handleCheckIn(member)}
      >
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
            isPresent ? "bg-green-100 dark:bg-green-900" : "bg-muted"
          }`}>
            {isPresent ? (
              <UserCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
            ) : (
              <UserX className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
          
          <div>
            <p className="font-medium">{member.full_name}</p>
            <p className="text-sm text-muted-foreground">{member.role}</p>
          </div>
        </div>

        <Badge variant={isPresent ? "default" : "outline"}>
          {isPresent ? "Present" : "Absent"}
        </Badge>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-4">Attendees</h2>
        <p className="text-muted-foreground">
          Check in members as they arrive. Quorum will be calculated automatically.
        </p>
      </div>

      {/* First Meeting Helper Banner */}
      {isFirstMeeting && (
        <Card className="p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <div className="flex items-start gap-3">
            <UserCheck className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <p className="font-medium text-green-800 dark:text-green-200">
                ✓ You're Already Checked In!
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                For your first organizational meeting, you're the only attendee required. 
                As the Secretary Treasurer, you're automatically checked in and ready to proceed.
                No additional members need to be added for this meeting.
              </p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-green-200 dark:border-green-700">
                <div className="h-5 w-5 rounded-full bg-green-600 dark:bg-green-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">→</span>
                </div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Next: Use the sidebar (or "Continue" button below) to move through Agenda, Motions, Action Items, and more.
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Quorum Settings */}
      <Card className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="quorum-required"
            checked={data.quorum_required}
            onCheckedChange={(checked) => onChange({ quorum_required: checked })}
          />
          <Label htmlFor="quorum-required">Quorum Required</Label>
        </div>

        {data.quorum_required && (
          <div className="space-y-2">
            <Label htmlFor="quorum-threshold">Quorum Threshold</Label>
            <Input
              id="quorum-threshold"
              type="number"
              min="1"
              value={data.quorum_threshold || ""}
              onChange={(e) => onChange({ quorum_threshold: parseInt(e.target.value) || null })}
              placeholder="Enter minimum number of members"
            />
          </div>
        )}
      </Card>

      {/* Officer Check-in (Secretary Treasurer) */}
      {treasurySecretary && (
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Officer Check-in
          </h3>
          
          <div className="grid gap-2">
            {renderAttendeeCard(treasurySecretary)}
          </div>
        </div>
      )}

      {/* Member Check-in */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <UsersIcon className="h-5 w-5" />
          Member Check-in
        </h3>
        
        <div className="grid gap-2">
          {unaMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No members found for this UNA.</p>
          ) : (
            unaMembers.map((member) => renderAttendeeCard(member))
          )}
        </div>
      </div>

      {/* Special Guests */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          Special Guests
        </h3>

        <Card className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="guest-name">Guest Name</Label>
              <Input
                id="guest-name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Enter guest name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="guest-title">Title (Optional)</Label>
              <Input
                id="guest-title"
                value={guestTitle}
                onChange={(e) => setGuestTitle(e.target.value)}
                placeholder="e.g., Legal Advisor"
              />
            </div>
          </div>

          <Button onClick={addGuest} disabled={!guestName.trim()}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Guest
          </Button>
        </Card>

        {data.special_guests.length > 0 && (
          <div className="mt-4 space-y-2">
            {data.special_guests.map((guest: any, index: number) => (
              <Card key={index} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium">{guest.name}</p>
                  {guest.title && (
                    <p className="text-sm text-muted-foreground">{guest.title}</p>
                  )}
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeGuest(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
